import {
  collection,
  getDocs,
  query,
  where,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "../firebase";
import { formatCount } from "../utils/formatCount";

const FIRESTORE_IN_LIMIT = 30;

const sendBroadcastFn = httpsCallable(functions, "sendBroadcast");
const getBroadcastQuotaFn = httpsCallable(functions, "getBroadcastQuota");

export const BROADCAST_TARGET_OPTIONS = [
  { value: "all", label: "All subscribers", shortLabel: "subscribers", price: null },
  { value: "supporter", label: "Supporter Circle only ($5/mo)", shortLabel: "Supporter Circle", price: "$5/mo" },
  { value: "inner", label: "Inner Circle only ($10/mo)", shortLabel: "Inner Circle", price: "$10/mo" },
  { value: "vip", label: "VIP only ($20/mo)", shortLabel: "VIP", price: "$20/mo" },
];

export function getTargetTierMeta(tier) {
  return BROADCAST_TARGET_OPTIONS.find((o) => o.value === tier)
    || BROADCAST_TARGET_OPTIONS[0];
}

export function formatReachLabel(count, tier = "all") {
  const n = Number(count) || 0;
  const meta = getTargetTierMeta(tier);
  if (tier === "all") {
    return n === 1 ? "1 subscriber" : `${formatCount(n)} subscribers`;
  }
  const name = meta.shortLabel;
  return n === 1 ? `1 ${name} member` : `${formatCount(n)} ${name} members`;
}

export function formatSendButtonLabel(count, tier = "all") {
  const n = Number(count) || 0;
  if (tier === "all") {
    return n === 1
      ? "Send to 1 subscriber"
      : `Send to all ${formatCount(n)} subscribers`;
  }
  const meta = getTargetTierMeta(tier);
  return n === 1
    ? `Send to 1 ${meta.shortLabel} member`
    : `Send to ${formatCount(n)} ${meta.shortLabel} members`;
}

export function formatHistoryReach(broadcast) {
  const count = typeof broadcast?.subscriberCount === "number"
    ? broadcast.subscriberCount
    : null;
  if (count == null) return "—";
  const tier = broadcast.targetTier || "all";
  return `${formatReachLabel(count, tier)} reached`;
}

function callableError(err) {
  return err?.message || err?.details || "Request failed.";
}

export async function fetchBroadcastsForCreators(creatorIds = []) {
  const ids = [...new Set(creatorIds.filter(Boolean))];
  if (!ids.length) return [];

  const broadcasts = [];
  for (let i = 0; i < ids.length; i += FIRESTORE_IN_LIMIT) {
    const batch = ids.slice(i, i + FIRESTORE_IN_LIMIT);
    const snap = await getDocs(query(
      collection(db, "broadcasts"),
      where("creatorId", "in", batch)
    ));
    snap.docs.forEach((d) => broadcasts.push({ id: d.id, ...d.data() }));
  }

  broadcasts.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
  return broadcasts;
}

export async function fetchCreatorBroadcastHistory(creatorId) {
  if (!creatorId) return [];
  const snap = await getDocs(query(
    collection(db, "broadcasts"),
    where("creatorId", "==", creatorId)
  ));
  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .sort((a, b) => {
      const aTs = a.sentAt?.seconds || a.createdAt?.seconds || 0;
      const bTs = b.sentAt?.seconds || b.createdAt?.seconds || 0;
      return bTs - aTs;
    });
}

export async function fetchBroadcastQuota() {
  try {
    const { data } = await getBroadcastQuotaFn();
    return data;
  } catch (err) {
    throw new Error(callableError(err));
  }
}

/** Server-side send to active subscribers (optionally tier-filtered) with weekly quota. */
export async function sendBroadcast(message, targetTier = "all") {
  try {
    const { data } = await sendBroadcastFn({ message, targetTier });
    return data;
  } catch (err) {
    throw new Error(callableError(err));
  }
}

/** @deprecated Use sendBroadcast — kept for any leftover callers. */
export async function createBroadcast({ text, message, targetTier }) {
  return sendBroadcast(text || message, targetTier || "all");
}

export async function getRecentBroadcasts(creatorIds = []) {
  return fetchBroadcastsForCreators(creatorIds);
}
